// Compute pile positions based on the board element's size.
//
// Default layout:
//   row 0: F0 F1 F2 F3  [Waste area] Stock (foundation on left, stock on right)
//   row 1: T0 T1 T2 T3 T4 T5 T6
//
// `foundationOnLeft = false` mirrors the top row: Stock [Waste area] F0 F1 F2 F3.

export interface Layout {
  cardW: number;
  cardH: number;
  stackGapDown: number;
  stackGapUp: number;
  wasteAreaW: number;
  wasteOffsetX: number;
  pilePositions: {
    stock: { x: number; y: number };
    waste: { x: number; y: number };
    foundation: Array<{ x: number; y: number }>;
    tableau: Array<{ x: number; y: number }>;
  };
}

const COLS = 7;

export function computeLayout(
  boardW: number,
  boardH: number,
  foundationOnLeft: boolean,
): Layout {
  const padding = 6;
  const horizontalGap = 2;
  const usableW = boardW - padding * 2;
  const cardW = Math.floor((usableW - horizontalGap * (COLS - 1)) / COLS);
  const cardH = Math.round(cardW * 1.47);
  const stackGapDown = Math.max(9, Math.round(cardH * 0.13));
  // Face-up offset needs to be tap-friendly on small phones — keep at least
  // ~22px exposed so a finger can reliably target a buried card.
  const stackGapUp = Math.max(22, Math.round(cardH * 0.3));

  const colX = (col: number) => padding + col * (cardW + horizontalGap);
  const twoColW = cardW * 2 + horizontalGap;
  const topRowY = padding;
  const bottomRowY = topRowY + cardH + Math.max(10, Math.round(cardH * 0.13));

  const tableau = Array.from({ length: COLS }, (_, i) => ({
    x: colX(i),
    y: bottomRowY,
  }));

  // Top row column allocation.
  // Stock sits on the outer edge; Waste sits between Stock and the foundation.
  // (Cards "flow out of" the stock toward the center as you draw.)
  let foundationCols: number[];
  let stockCol: number;
  let wasteCol: number;
  if (foundationOnLeft) {
    foundationCols = [0, 1, 2, 3];
    wasteCol = 4;
    stockCol = 6;
  } else {
    stockCol = 0;
    wasteCol = 1;
    foundationCols = [3, 4, 5, 6];
  }

  const foundation = foundationCols.map((c) => ({ x: colX(c), y: topRowY }));
  const wastePaddingX = Math.round(twoColW * 0.05);
  const wasteUsableW = twoColW - wastePaddingX * 2;
  const wasteOffsetX = Math.max(0, Math.round((wasteUsableW - cardW) / 2));

  void boardH;
  return {
    cardW,
    cardH,
    stackGapDown,
    stackGapUp,
    wasteAreaW: twoColW,
    wasteOffsetX,
    pilePositions: {
      stock: { x: colX(stockCol), y: topRowY },
      waste: { x: colX(wasteCol) + wastePaddingX, y: topRowY },
      foundation,
      tableau,
    },
  };
}
